import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnInit,
  ViewEncapsulation,
  inject,
  runInInjectionContext,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormControlStatus, ValidationErrors } from '@angular/forms';
import { ThemeAssetComponent } from '@partners/themes';
import { FIELD_ERROR_ANIMATION } from '@partners/ui';
import { filter, isObservable, map, of, startWith, switchMap, tap } from 'rxjs';
import { IFormField } from '../../model';

/**
 * @description
 * wrapper for the form field hint messages.
 *
 * purpose of this component:
 * 1). it toggles the display of hints containers (only one of them is displays any given moment)
 */

@Component({
  standalone: true,
  selector: 'field-hint',
  template: `
    @if (hint()) {
    <span class="subscript-text">{{ hint() }}</span>
    }
  `,
  animations: [FIELD_ERROR_ANIMATION],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [AsyncPipe, ThemeAssetComponent],
  host: {
    class: 'field-subscript'
  }
})
export class FieldHintComponent<T> implements OnInit {
  @Input()
  fieldConfig: IFormField<T>;

  @Input()
  control: FormControl;

  hint = signal<string | undefined>(undefined);

  #changeDetector = inject(ChangeDetectorRef);
  #localInjector = inject(Injector);

  ngOnInit(): void {
    this.#addStateListeners();
  }

  #addStateListeners() {
    const { control, hint } = this.fieldConfig;

    const targetControl = this.control || control;

    runInInjectionContext(this.#localInjector, () => {
      toSignal(
        targetControl.statusChanges.pipe(
          startWith(null),
          filter((status: FormControlStatus) => {
            return status !== 'PENDING';
          }),
          map((status) => {
            if (status !== 'VALID') {
              return targetControl?.errors;
            }
            return null;
          }),
          switchMap((rawErrors: ValidationErrors) => {
            const [firstKey, ...remainingKeys] = Object.keys(rawErrors || []);
            const { pristine, touched } = targetControl;

            /** while the field is untouched, and has no errors, we show the hint text */
            if (!firstKey || (pristine && !touched)) {
              return isObservable(hint) ? hint : of(hint);
            }

            return of(undefined);
          }),
          tap((hintText: string | undefined) => {
            this.hint.set(hintText);
            this.#changeDetector.detectChanges();
          })
        )
      );
    });
  }
}
